import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addInspectionReportEdit,
  createInspectionReport,
  createPreInspection,
  getAllowedTransitions,
  getInspectionReport,
  getJobStatusHistory,
  getPreInspection,
  getPreInspectionQcResult,
  listPartners,
  listPaidPreInspections,
  listPreInspections,
  updatePreInspection,
  searchJobs,
  submitInspectionReport,
  updateJobStatus,
  type AllowedTransitionsResponse,
  type InspectionReportCreateRequest,
  type InspectionReportEditRequest,
  type InspectionReportEditResponse,
  type InspectionReportResponse,
  type JobSearchParams,
  type JobSearchResponse,
  type JobStatusHistoryResponse,
  type ListParams,
  type PaidListParams,
  type Partner,
  type PreInspectionQcResultResponse,
  type UpdateJobStatusRequest,
  type UpdateJobStatusResponse,
} from '@/lib/pre-inspection/api';
import type { PaidPreInspectionListResponse } from '@/types/paid-pre-inspection';
import type {
  PreInspectionCreateRequest,
  PreInspectionCreateResponse,
  PreInspectionDetailResponse,
  PreInspectionUpdateRequest,
} from '@/types/pre-inspection';

export const preInspectionKeys = {
  all: ['pre-inspections'] as const,
  list: (params: ListParams) => [...preInspectionKeys.all, 'list', params] as const,
  detail: (jobId: string) => [...preInspectionKeys.all, 'detail', jobId] as const,
  transitions: (statusCode: string) =>
    [...preInspectionKeys.all, 'transitions', statusCode] as const,
  statusHistory: (jobId: string) =>
    [...preInspectionKeys.all, 'status-history', jobId] as const,
  partners: () => [...preInspectionKeys.all, 'partners'] as const,
  jobSearch: (params: JobSearchParams) =>
    [...preInspectionKeys.all, 'job-search', params] as const,
  qcResult: (jobId: string) =>
    [...preInspectionKeys.all, 'qc-result', jobId] as const,
  report: (reportId: string) =>
    [...preInspectionKeys.all, 'report', reportId] as const,
};

export interface HookGateOptions {
  /** Skip the query entirely when false (e.g. feature flag off). Defaults to true. */
  enabled?: boolean;
}

export function usePreInspectionList(
  params: ListParams = { limit: 20 },
  options: HookGateOptions = {},
) {
  const { enabled = true } = options;
  return useQuery({
    queryKey: preInspectionKeys.list(params),
    queryFn: () => listPreInspections(params),
    enabled,
  });
}

export function useJobSearch(
  params: JobSearchParams,
  options: HookGateOptions = {},
) {
  const { enabled = true } = options;
  return useQuery<JobSearchResponse>({
    queryKey: preInspectionKeys.jobSearch(params),
    queryFn: () => searchJobs(params),
    enabled,
    staleTime: 30_000,
  });
}

export function usePreInspectionQcResult(
  jobId: string | undefined,
  options: HookGateOptions = {},
) {
  const { enabled = true } = options;
  return useQuery<PreInspectionQcResultResponse>({
    queryKey: preInspectionKeys.qcResult(jobId ?? ''),
    queryFn: () => getPreInspectionQcResult(jobId as string),
    enabled: enabled && Boolean(jobId),
    staleTime: 30_000,
  });
}

export function usePreInspectionDetail(
  jobId: string | undefined,
  options: HookGateOptions = {},
) {
  const { enabled = true } = options;
  return useQuery({
    queryKey: preInspectionKeys.detail(jobId ?? ''),
    queryFn: () => getPreInspection(jobId as string),
    enabled: enabled && Boolean(jobId),
  });
}

export function useCreatePreInspection(options?: {
  onSuccess?: (data: PreInspectionCreateResponse) => void;
}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: PreInspectionCreateRequest) => createPreInspection(payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: preInspectionKeys.all });
      options?.onSuccess?.(data);
    },
  });
}

export function useUpdatePreInspection(
  jobId: string,
  options?: { onSuccess?: (data: PreInspectionDetailResponse) => void },
) {
  const queryClient = useQueryClient();
  return useMutation<PreInspectionDetailResponse, unknown, PreInspectionUpdateRequest>({
    mutationFn: (payload) => updatePreInspection(jobId, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: preInspectionKeys.detail(jobId) });
      queryClient.invalidateQueries({ queryKey: [...preInspectionKeys.all, 'list'] });
      options?.onSuccess?.(data);
    },
  });
}

export function usePartners(options: HookGateOptions = {}) {
  const { enabled = true } = options;
  return useQuery<Partner[]>({
    queryKey: preInspectionKeys.partners(),
    queryFn: () => listPartners(),
    enabled,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useJobStatusHistory(
  jobId: string | undefined,
  options: HookGateOptions = {},
) {
  const { enabled = true } = options;
  return useQuery<JobStatusHistoryResponse>({
    queryKey: preInspectionKeys.statusHistory(jobId ?? ''),
    queryFn: () => getJobStatusHistory(jobId as string),
    enabled: enabled && Boolean(jobId),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

export function useAllowedTransitions(
  fromStatusCode: string | undefined,
  options: HookGateOptions = {},
) {
  const { enabled = true } = options;
  return useQuery<AllowedTransitionsResponse>({
    queryKey: preInspectionKeys.transitions(fromStatusCode ?? ''),
    queryFn: () => getAllowedTransitions(fromStatusCode as string),
    enabled: enabled && Boolean(fromStatusCode),
    staleTime: 30_000,
  });
}

export function useUpdateJobStatus(
  jobId: string,
  options?: {
    onSuccess?: (data: UpdateJobStatusResponse) => void;
  },
) {
  const queryClient = useQueryClient();
  return useMutation<UpdateJobStatusResponse, unknown, UpdateJobStatusRequest>({
    mutationFn: (payload) => updateJobStatus(jobId, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: preInspectionKeys.detail(jobId) });
      queryClient.invalidateQueries({
        queryKey: [...preInspectionKeys.all, 'transitions'],
      });
      queryClient.invalidateQueries({
        queryKey: preInspectionKeys.statusHistory(jobId),
      });
      options?.onSuccess?.(data);
    },
  });
}

export function useInspectionReport(
  reportId: string | undefined,
  options: HookGateOptions = {},
) {
  const { enabled = true } = options;
  return useQuery<InspectionReportResponse>({
    queryKey: preInspectionKeys.report(reportId ?? ''),
    queryFn: () => getInspectionReport(reportId as string),
    enabled: enabled && Boolean(reportId),
    staleTime: 30_000,
  });
}

export function useCreateInspectionReport(options?: {
  onSuccess?: (data: InspectionReportResponse) => void;
}) {
  return useMutation<InspectionReportResponse, unknown, InspectionReportCreateRequest>({
    mutationFn: (payload) => createInspectionReport(payload),
    onSuccess: (data) => options?.onSuccess?.(data),
  });
}

export function useAddInspectionReportEdit(
  reportId: string,
  options?: { onSuccess?: (data: InspectionReportEditResponse) => void },
) {
  const queryClient = useQueryClient();
  return useMutation<InspectionReportEditResponse, unknown, InspectionReportEditRequest>({
    mutationFn: (edit) => addInspectionReportEdit(reportId, edit),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: preInspectionKeys.report(reportId) });
      options?.onSuccess?.(data);
    },
  });
}

export const paidPreInspectionKeys = {
  all: ['paid-pre-inspections'] as const,
  list: (params: PaidListParams) => [...paidPreInspectionKeys.all, 'list', params] as const,
};

export function usePaidPreInspections(
  params: PaidListParams = {},
  options: HookGateOptions = {},
) {
  const { enabled = true } = options;
  return useQuery<PaidPreInspectionListResponse>({
    queryKey: paidPreInspectionKeys.list(params),
    queryFn: () => listPaidPreInspections(params),
    enabled,
    staleTime: 30_000,
  });
}

export function useSubmitInspectionReport(
  reportId: string,
  options?: { onSuccess?: (data: InspectionReportResponse) => void },
) {
  const queryClient = useQueryClient();
  return useMutation<InspectionReportResponse, unknown, void>({
    mutationFn: () => submitInspectionReport(reportId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: preInspectionKeys.report(reportId) });
      options?.onSuccess?.(data);
    },
  });
}
